import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const WS_BASE = (() => {
  const u = new URL(BACKEND_URL);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${u.origin}/api/ws`;
})();

// Public endpoints that legitimately return 401 to anonymous callers (failed
// login attempts, wrong kiosk PIN, etc.). A 401 from these MUST NOT log the
// current admin/employee out — these are not session expirations.
const PUBLIC_ENDPOINTS = [
  '/auth/login',
  '/auth/google/',
  '/employee-auth/login',
  '/clock/pin',
  '/clock/kiosk/',
  '/public/signup',
];

// Frontend routes that are themselves anonymous. Even if some call returns
// 401 from such a page, we must not hard-redirect away from it.
const PUBLIC_PATHS = ['/login', '/signup', '/portal/login', '/kiosk', '/auth/google'];

function isPublicRequestUrl(url) {
  if (!url) return false;
  return PUBLIC_ENDPOINTS.some((p) => url.includes(p));
}

function isOnPublicPath() {
  const path = window.location.pathname || '';
  return PUBLIC_PATHS.some((p) => path === p || path.startsWith(`${p}/`) || path.startsWith(p));
}

export const api = axios.create({ baseURL: API_BASE, timeout: 30000 });

// ---------------------------------------------------------------------------
// Mutation bus — a tiny cross-page signal so screens like the Payroll page can
// know when data they depend on has changed and refetch without a page
// reload. Any 2xx response to a mutating call against a payroll-affecting
// endpoint fires a `data-changed` CustomEvent with `{ scope }` in `detail`.
//
// Consumers (e.g. PayrollPage) simply do:
//     dataChangeBus.addEventListener('data-changed', handler);
// and receive events like:
//     { detail: { scope: 'employees' | 'attendance' } }
//
// Deliberately kept scoped to *this browser tab* — cross-tab live updates
// still come through the existing tenant WebSocket (attendance.clock_in etc).
// ---------------------------------------------------------------------------
export const dataChangeBus =
  typeof window !== 'undefined' ? new EventTarget() : null;

// Endpoints whose *successful* mutations (POST / PATCH / DELETE) should
// invalidate any dependent payroll-page state. Matched by substring against
// the request URL as it appears in the axios config (i.e. without the
// leading API_BASE).
const PAYROLL_AFFECTING_ENDPOINTS = [
  { match: '/employees', scope: 'employees' },
  { match: '/attendance', scope: 'attendance' },
  { match: '/clock/pin', scope: 'attendance' },
];

function _mutationScopeFor(config) {
  const method = (config?.method || '').toUpperCase();
  if (!['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) return null;
  const url = config?.url || '';
  for (const entry of PAYROLL_AFFECTING_ENDPOINTS) {
    if (url.includes(entry.match)) return entry.scope;
  }
  return null;
}

function _fireDataChanged(scope) {
  if (!dataChangeBus || !scope) return;
  try {
    dataChangeBus.dispatchEvent(new CustomEvent('data-changed', { detail: { scope } }));
  } catch {
    /* fail-open — never break a mutation because the bus errored */
  }
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('rota_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => {
    // Fire the mutation bus on any successful admin-side mutation of
    // employees, attendance or clock. See PAYROLL_AFFECTING_ENDPOINTS above.
    const scope = _mutationScopeFor(res?.config);
    if (scope) _fireDataChanged(scope);
    return res;
  },
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url;
    // Only treat 401 as a session-expiry signal when:
    //   * the call was to a private endpoint (not /auth/login, /clock/*, etc.)
    //   * AND the user is currently on a private page (not /login, /kiosk, /portal/login)
    // Otherwise this is just a regular "bad creds" / "bad PIN" failure — let
    // the calling page show its own inline error.
    if (status === 401 && !isPublicRequestUrl(url) && !isOnPublicPath()) {
      localStorage.removeItem('rota_token');
      localStorage.removeItem('rota_user');
      window.location.href = '/login?expired=1';
    }
    // 402 with code=trial_expired: the tenant's 14-day self-signup trial has
    // ended. Route the manager to the upgrade wall (their data is retained;
    // billing endpoints stay reachable so they can pay).
    const detail = error?.response?.data?.detail;
    if (
      status === 402 &&
      detail && typeof detail === 'object' && detail.code === 'trial_expired' &&
      !window.location.pathname.startsWith('/upgrade') &&
      !window.location.pathname.startsWith('/billing')
    ) {
      window.location.href = '/upgrade';
    }
    return Promise.reject(error);
  }
);

// Separate axios instance for the EMPLOYEE PORTAL so that an admin and an
// employee can be logged in on the same browser without colliding.
export const employeeApi = axios.create({ baseURL: API_BASE, timeout: 30000 });

// Portal shell needs slightly more session fields than the admin shell to
// render the restaurant header without a flash on refresh (name +
// slug + currency for currency-formatting). Everything else — email,
// phone, salary, hourly rate — is fetched via ``/employee-auth/me`` at
// mount time and lives only in React state.
const EMPLOYEE_SESSION_WHITELIST = [
  'id', 'name', 'restaurant_id', 'restaurant_name', 'restaurant_slug',
  'currency', 'timezone', 'employment_type', 'weekly_hours_target',
];

export function setEmployeeSession(token, employee) {
  localStorage.setItem('rota_employee_token', token);
  localStorage.setItem(
    'rota_employee',
    JSON.stringify(_pickSessionFields(employee, EMPLOYEE_SESSION_WHITELIST)),
  );
}

export function clearEmployeeSession() {
  localStorage.removeItem('rota_employee_token');
  localStorage.removeItem('rota_employee');
}

export function getStoredEmployee() {
  try {
    const raw = localStorage.getItem('rota_employee');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

employeeApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('rota_employee_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

employeeApi.interceptors.response.use(
  (res) => {
    // Employees clocking in/out via portal also touches attendance.
    const scope = _mutationScopeFor(res?.config);
    if (scope) _fireDataChanged(scope);
    return res;
  },
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url;
    // Same logic: only redirect on real session expirations from /portal pages.
    if (
      status === 401 &&
      !isPublicRequestUrl(url) &&
      window.location.pathname.startsWith('/portal') &&
      !window.location.pathname.startsWith('/portal/login')
    ) {
      localStorage.removeItem('rota_employee_token');
      localStorage.removeItem('rota_employee');
      window.location.href = '/portal/login?expired=1';
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Session cache — minimum-PII shape
// ---------------------------------------------------------------------------
// Storing the full server-side ``user`` blob in localStorage puts PII (email,
// employment_type etc.) in reach of any XSS on the page. We only need enough
// to render the top nav / avatar synchronously on refresh — the rest is
// fetched from ``GET /auth/me`` on mount by the AuthContext.
//
// Kept intentionally tiny: id, name, role, and (for tenant admins) the
// restaurant_id so tenant-scoped requests can be made before ``/me`` returns.
const ADMIN_SESSION_WHITELIST = ['id', 'name', 'role', 'restaurant_id'];

function _pickSessionFields(user, whitelist) {
  if (!user || typeof user !== 'object') return null;
  const out = {};
  for (const field of whitelist) {
    if (user[field] !== undefined) out[field] = user[field];
  }
  return out;
}

export function setSession(token, user) {
  localStorage.setItem('rota_token', token);
  // Only the minimum required to render synchronously on refresh — see the
  // comment above. Full profile lives in React state (fetched via /me) and
  // is never persisted.
  localStorage.setItem(
    'rota_user',
    JSON.stringify(_pickSessionFields(user, ADMIN_SESSION_WHITELIST)),
  );
}

export function clearSession() {
  localStorage.removeItem('rota_token');
  localStorage.removeItem('rota_user');
}

export function getStoredUser() {
  try {
    const raw = localStorage.getItem('rota_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getStoredToken() {
  return localStorage.getItem('rota_token');
}

export function extractErrorMessage(err, fallback = 'Something went wrong') {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (typeof data === 'string') return data;
  if (typeof data?.detail === 'string') return data.detail;
  if (Array.isArray(data?.detail)) {
    return data.detail.map((d) => d?.msg || d?.message || JSON.stringify(d)).join(', ');
  }
  return fallback;
}
